import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Row,
  Col
} from 'reactstrap';
import instance from '../../api/Axios';

const BMOComponents = () => {
  const [componentTypes, setComponentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modal, setModal] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    is_active: true
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchComponentTypes();
  }, []);

  const fetchComponentTypes = async () => {
    try {
      setLoading(true);
      // Fetch BMO-specific component types
      const response = await instance.get('/api/component-types/?bmo_only=true');
      setComponentTypes(response.data.results || response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching BMO component types:', err);
      setError('Failed to load component types');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Component name is required';
    }
    
    if (!formData.code.trim()) {
      newErrors.code = 'Component code is required';
    } else if (!/^[A-Z0-9_]+$/.test(formData.code)) {
      newErrors.code = 'Code must contain only uppercase letters, numbers, and underscores';
    }
    
    // Check for duplicate names and codes (excluding current editing item)
    const duplicateName = componentTypes.find(ct => 
      ct.name.toLowerCase() === formData.name.toLowerCase() && 
      ct.id !== editingComponent?.id
    );
    if (duplicateName) {
      newErrors.name = 'A component type with this name already exists';
    }
    
    const duplicateCode = componentTypes.find(ct => 
      ct.code === formData.code && 
      ct.id !== editingComponent?.id
    );
    if (duplicateCode) {
      newErrors.code = 'A component type with this code already exists';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      setError('');
      
      const payload = {
        ...formData,
        bmo_only: true // This indicates it's a BMO-specific component
      };
      
      if (editingComponent) {
        await instance.put(`/api/component-types/${editingComponent.id}/`, payload);
        setSuccess('Component type updated successfully');
      } else {
        await instance.post('/api/component-types/', payload);
        setSuccess('Component type created successfully');
      }
      
      toggleModal();
      fetchComponentTypes();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving component type:', err);
      if (err.response?.data) {
        if (typeof err.response.data === 'object') {
          setErrors(err.response.data);
        } else {
          setError(err.response.data);
        }
      } else {
        setError('Failed to save component type');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (componentType) => {
    if (!window.confirm(`Are you sure you want to delete "${componentType.name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setSubmitting(true);
      await instance.delete(`/api/component-types/${componentType.id}/`);
      setSuccess('Component type deleted successfully');
      fetchComponentTypes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting component type:', err);
      setError('Failed to delete component type');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      // Opening modal
      setFormData({
        name: editingComponent?.name || '',
        code: editingComponent?.code || '',
        description: editingComponent?.description || '',
        is_active: editingComponent?.is_active ?? true
      });
      setErrors({});
    } else {
      // Closing modal
      setEditingComponent(null);
      setFormData({ name: '', code: '', description: '', is_active: true });
      setErrors({});
    }
  };

  const handleEdit = (componentType) => {
    setEditingComponent(componentType);
    setModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value: rawValue, type, checked } = e.target;
    // normalize code to uppercase (textTransform only affects visual display)
    const value = name === 'code' && typeof rawValue === 'string' ? rawValue.toUpperCase().trim() : (type === 'checkbox' ? checked : rawValue);

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="page-wrapper">
      <Row>
        <Col lg="12">
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <div>
                <CardTitle tag="h5" className="mb-0">
                  <i className="bi bi-puzzle me-2"></i>
                  BMO Component Types
                </CardTitle>
                <small className="text-muted">
                  Manage component types specific to your BMO. These will be available alongside global components.
                </small>
              </div>
              <Button
                color="primary"
                onClick={() => {
                  setEditingComponent(null);
                  toggleModal();
                }}
                disabled={submitting}
              >
                <i className="bi bi-plus me-2"></i>
                Add Component Type
              </Button>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              )}
              
              {success && (
                <Alert color="success" className="mb-3">
                  <i className="bi bi-check-circle me-2"></i>
                  {success}
                </Alert>
              )}

              {loading ? (
                <div className="text-center p-4">
                  <Spinner color="primary" />
                  <p className="mt-2">Loading component types...</p>
                </div>
              ) : componentTypes.length === 0 ? (
                <div className="text-center p-4">
                  <i className="bi bi-puzzle display-1 text-muted mb-3"></i>
                  <h5 className="text-muted">No BMO Component Types</h5>
                  <p className="text-muted mb-3">
                    You haven't created any BMO-specific component types yet.
                  </p>
                  <Button
                    color="primary"
                    onClick={() => {
                      setEditingComponent(null);
                      toggleModal();
                    }}
                  >
                    <i className="bi bi-plus me-2"></i>
                    Create Your First Component Type
                  </Button>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Code</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {componentTypes.map((componentType) => (
                        <tr key={componentType.id}>
                          <td>
                            <strong>{componentType.name}</strong>
                          </td>
                          <td>
                            <Badge color="secondary">{componentType.code}</Badge>
                          </td>
                          <td>
                            {componentType.description || (
                              <span className="text-muted">No description</span>
                            )}
                          </td>
                          <td>
                            <Badge color={componentType.is_active ? 'success' : 'secondary'}>
                              {componentType.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>{formatDate(componentType.created_at)}</td>
                          <td>
                            <UncontrolledDropdown>
                              <DropdownToggle
                                caret
                                size="sm"
                                color="outline-primary"
                                disabled={submitting}
                              >
                                Actions
                              </DropdownToggle>
                              <DropdownMenu>
                                <DropdownItem onClick={() => handleEdit(componentType)}>
                                  <i className="bi bi-pencil me-2"></i>
                                  Edit
                                </DropdownItem>
                                <DropdownItem divider />
                                <DropdownItem
                                  className="text-danger"
                                  onClick={() => handleDelete(componentType)}
                                >
                                  <i className="bi bi-trash me-2"></i>
                                  Delete
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Modal */}
      <Modal isOpen={modal} toggle={toggleModal} size="md">
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={toggleModal}>
            <i className="bi bi-puzzle me-2"></i>
            {editingComponent ? 'Edit Component Type' : 'Add Component Type'}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="name">
                Component Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="name"
                id="name"
                value={formData.name}
                onChange={handleInputChange}
                invalid={!!errors.name}
                placeholder="e.g., Mobile Phone Allowance"
                required
              />
              {errors.name && (
                <div className="invalid-feedback d-block">{errors.name}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="code">
                Component Code <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="code"
                id="code"
                value={formData.code}
                onChange={handleInputChange}
                invalid={!!errors.code}
                placeholder="e.g., MOBILE_PHONE"
                required
                style={{ textTransform: 'uppercase' }}
              />
              {errors.code && (
                <div className="invalid-feedback d-block">{errors.code}</div>
              )}
              <small className="text-muted">
                Use uppercase letters, numbers, and underscores only
              </small>
            </FormGroup>

            <FormGroup>
              <Label for="description">Description</Label>
              <Input
                type="textarea"
                name="description"
                id="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Describe what this component covers..."
              />
            </FormGroup>

            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <span className="form-check-label">Active</span>
              </Label>
              <small className="text-muted d-block">
                Only active component types can be assigned to bursaries
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="secondary" onClick={toggleModal}>
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {editingComponent ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <i className="bi bi-check me-2"></i>
                  {editingComponent ? 'Update' : 'Create'}
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default BMOComponents;